import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Form,
    Input,
    Popconfirm,
    Space,
    Table,
    Tabs,
    Tag,
    Typography,
    message,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

import AdminUsersAPI from '../api/adminUsers';

const { Paragraph, Text } = Typography;

const EVENT_COLOR = {
    delegate_reset: 'orange',
    admin_reset: 'red',
    delegate_grant: 'green',
    delegate_revoke: 'default',
};

function formatDate(s) {
    if (!s) return '';
    try {
        return new Date(s).toLocaleString();
    } catch (e) {
        return s;
    }
}

function DelegateList({ messageApi }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [granting, setGranting] = useState(false);
    const [form] = Form.useForm();

    const refresh = useCallback(() => {
        setLoading(true);
        AdminUsersAPI.listDelegates(
            (res) => {
                setRows(res || []);
                setLoading(false);
            },
            (err) => {
                console.log(err);
                setLoading(false);
                messageApi.error(err?.detail || 'Failed to load');
            }
        );
    }, [messageApi]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const onGrant = ({ username, note }) => {
        setGranting(true);
        AdminUsersAPI.grantDelegate(
            username.trim(),
            (note || '').trim(),
            () => {
                setGranting(false);
                messageApi.success(`${username} can now reset 2FA`);
                form.resetFields();
                refresh();
            },
            (err) => {
                console.log(err);
                setGranting(false);
                messageApi.error(err?.detail || 'Grant failed');
            }
        );
    };

    const onRevoke = (row) => {
        AdminUsersAPI.revokeDelegate(
            row.username,
            () => {
                messageApi.success(`Revoked ${row.username}`);
                refresh();
            },
            (err) => {
                console.log(err);
                messageApi.error(err?.detail || 'Revoke failed');
            }
        );
    };

    const columns = [
        {
            title: 'Username',
            dataIndex: 'username',
            render: (v, row) => <Space>
                <Text strong>{v}</Text>
                {row.is_admin && <Tag color="red">Administrator</Tag>}
                {row.disable && <Tag>Disabled</Tag>}
            </Space>,
        },
        {
            title: 'Name',
            render: (_, row) =>
                [row.firstname, row.lastname].filter(Boolean).join(' ') || '—',
        },
        { title: 'Email', dataIndex: 'email', render: (v) => v || '—' },
        {
            title: '',
            key: 'actions',
            align: 'right',
            render: (_, row) => <Popconfirm
                title={`Revoke ${row.username}?`}
                description="They lose the ability to reset 2FA immediately."
                okText="Revoke"
                okButtonProps={{ danger: true }}
                onConfirm={() => onRevoke(row)}
            >
                <Button size="small" danger>Revoke</Button>
            </Popconfirm>,
        },
    ];

    return <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Alert
            type="info"
            showIcon
            message="Delegates can reset 2FA — nothing else."
            description={
                'A delegate cannot reset an administrator, another delegate, or '
                + 'themselves, must record a reason, and is limited to 20 resets per day. '
                + 'Revoking takes effect on their next request, without waiting for logout.'
            }
        />
        <Card
            size="small"
            title="Grant delegation"
            extra={<Button size="small" icon={<ReloadOutlined />} onClick={refresh} />}
        >
            <Form form={form} onFinish={onGrant} layout="inline">
                <Form.Item name="username" rules={[{ required: true }]}>
                    <Input placeholder="username" allowClear />
                </Form.Item>
                <Form.Item name="note" style={{ flex: 1, minWidth: 200 }}>
                    <Input placeholder="note (optional) — e.g. ENG helpdesk" maxLength={255} />
                </Form.Item>
                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={granting}>
                        Grant
                    </Button>
                </Form.Item>
            </Form>
        </Card>
        <Table
            rowKey="username"
            size="small"
            loading={loading}
            columns={columns}
            dataSource={rows}
            pagination={false}
            locale={{ emptyText: 'No delegates yet' }}
        />
    </Space>;
}

function AuditLog({ messageApi }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({});

    const refresh = useCallback(() => {
        setLoading(true);
        AdminUsersAPI.listAudit(
            filters,
            (res) => {
                setRows(res || []);
                setLoading(false);
            },
            (err) => {
                console.log(err);
                setLoading(false);
                messageApi.error(err?.detail || 'Failed to load');
            }
        );
    }, [filters, messageApi]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const columns = [
        { title: 'When', dataIndex: 'created_at', render: formatDate, width: 180 },
        {
            title: 'Event',
            dataIndex: 'event',
            render: (v) => <Tag color={EVENT_COLOR[v]}>{v}</Tag>,
        },
        { title: 'Subject', dataIndex: 'username' },
        { title: 'Actor', dataIndex: 'actor_username', render: (v) => v || '—' },
        { title: 'Reason / note', dataIndex: 'note', render: (v) => v || '—' },
        { title: 'IP', dataIndex: 'ip', render: (v) => v || '—' },
    ];

    return <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Last 90 days of 2FA activity. Filter by the account acted on (subject)
            or by the operator who acted (actor).
        </Paragraph>
        <Space wrap>
            <Input.Search
                placeholder="subject username"
                allowClear
                onSearch={(v) => setFilters((f) => ({ ...f, username: v || undefined }))}
                style={{ width: 220 }}
            />
            <Input.Search
                placeholder="actor username"
                allowClear
                onSearch={(v) => setFilters((f) => ({ ...f, actor: v || undefined }))}
                style={{ width: 220 }}
            />
            <Button icon={<ReloadOutlined />} onClick={refresh}>Refresh</Button>
        </Space>
        <Table
            rowKey="id"
            size="small"
            loading={loading}
            columns={columns}
            dataSource={rows}
            pagination={{ pageSize: 25, showSizeChanger: false }}
            scroll={{ x: 'max-content' }}
        />
    </Space>;
}

export default function AdminDelegates() {
    const [messageApi, contextHolder] = message.useMessage();

    return <Card title="Admin — 2FA Delegates">
        {contextHolder}
        <Tabs
            defaultActiveKey="delegates"
            items={[
                {
                    key: 'delegates',
                    label: 'Delegates',
                    children: <DelegateList messageApi={messageApi} />,
                },
                {
                    key: 'audit',
                    label: 'Audit log',
                    children: <AuditLog messageApi={messageApi} />,
                },
            ]}
        />
    </Card>;
}
